import { createSettingMetadataFromGroupDefaults } from "../../settings-catalog/metadata";
import type {
  SettingDefinition,
  SettingMetadata,
  SettingRegistryMetadataInput,
  TeamSettingsSettingValueSource,
} from "../../settings-catalog/types";
import {
  OrderTrackingSettingsFlatTeamSettingGroupById,
  type OrderTrackingSettingsFlatTeamSettingGroupId,
} from "./groups";

type OrderTrackingSettingsSettingInput = SettingRegistryMetadataInput;

function setting(
  metadata: OrderTrackingSettingsSettingInput,
): OrderTrackingSettingsSettingInput {
  return metadata;
}

function orderTrackingSettingsGroup<
  const Group extends OrderTrackingSettingsFlatTeamSettingGroupId,
  const T extends Record<string, OrderTrackingSettingsSettingInput>,
>(group: Group, settings: T): { [K in keyof T]: T[K] & { group: Group } } {
  // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
  return Object.fromEntries(
    Object.entries(settings).map(([key, value]) => [key, { ...value, group }]),
  ) as { [K in keyof T]: T[K] & { group: Group } };
}

// Keys must be real flat schema keys from ./settings.ts (wired into
// FlatTeamSettingsSchema). Each equals the team.settings.* path, dots→slashes.
const OrderTrackingSettingsRegistry = {
  ...orderTrackingSettingsGroup("order_tracking_settings.general", {
    "orderTracking/textRules": setting({
      label: "Tracking text rules",
      description:
        "Hide or replace matching words and phrases in customer tracking messages, locations, or all supported fields. Hidden messages show the status; events and dates remain visible. Maps are hidden when their location matches a rule.",
      synonyms: [
        "tracking text rules",
        "replace tracking phrases",
        "hidden tracking locations",
      ],
      risk: {
        level: "medium",
        reason:
          "Changes the shipment information shown to customers on all tracking pages.",
      },
    }),
    "orderTracking/stalledDays": setting({
      label: "Stalled in transit threshold",
      description:
        "Number of days after which an order is considered stalled in transit and may require attention.",
      synonyms: [
        "stalled in transit",
        "transit threshold",
        "stalled days",
        "stalled transit threshold",
        "when is an order stalled in transit",
        "days until stalled in transit",
      ],
    }),
    "orderTracking/stalledDaysFulfillment": setting({
      label: "Stalled in fulfillment threshold",
      description:
        "Number of days after which an unfulfilled order is considered stalled and may require attention.",
      synonyms: [
        "stalled in fulfillment",
        "fulfillment threshold",
        "stalled fulfillment days",
        "unfulfilled order flagged",
        "days until stalled in fulfillment",
      ],
    }),
    "orderTracking/v2/excludeSaturdayTransit": setting({
      label: "Exclude Saturdays from transit threshold",
      description:
        "Exclude Saturdays when counting days toward the stalled-in-transit threshold.",
      synonyms: [
        "exclude saturday transit",
        "skip saturdays transit",
        "weekend transit exclusion",
      ],
    }),
    "orderTracking/v2/excludeSundayTransit": setting({
      label: "Exclude Sundays from transit threshold",
      description:
        "Exclude Sundays when counting days toward the stalled-in-transit threshold.",
      synonyms: [
        "exclude sunday transit",
        "skip sundays transit",
        "weekend transit exclusion",
      ],
    }),
    "orderTracking/v2/excludeSaturdayFulfillment": setting({
      label: "Exclude Saturdays from fulfillment threshold",
      description:
        "Exclude Saturdays when counting days toward the stalled-in-fulfillment threshold.",
      synonyms: [
        "exclude saturday fulfillment",
        "skip saturdays fulfillment",
        "weekend fulfillment exclusion",
      ],
    }),
    "orderTracking/v2/excludeSundayFulfillment": setting({
      label: "Exclude Sundays from fulfillment threshold",
      description:
        "Exclude Sundays when counting days toward the stalled-in-fulfillment threshold.",
      synonyms: [
        "exclude sunday fulfillment",
        "skip sundays fulfillment",
        "weekend fulfillment exclusion",
      ],
    }),
    "orderTracking/v2/orderExclusionRules": setting({
      label: "Order exclusion rules",
      description:
        "Conditions that exclude matching orders from stalled-order monitoring.",
      synonyms: [
        "order exclusion rules",
        "exclusion conditions",
        "exclude orders",
        "order tracking exclusions",
      ],
    }),
  }),
  ...orderTrackingSettingsGroup(
    "order_tracking_settings.checkout_delivery_estimates",
    {
      "checkoutDeliveryEstimates/enabled": setting({
        label: "Show estimated delivery dates at checkout",
        description:
          "Display estimated delivery dates next to shipping methods at checkout. Turning this off hides estimates without deleting the existing configuration.",
        synonyms: [
          "estimated delivery dates at checkout",
          "checkout delivery date toggle",
        ],
        valueSemantics: {
          trueLabel: "Shown at checkout",
          falseLabel: "Hidden at checkout",
        },
      }),
    },
  ),
  ...orderTrackingSettingsGroup("order_tracking_settings.carrier_mappings", {
    "orderTracking/carrierMappings": setting({
      label: "Carrier mappings",
      description:
        "Rules that normalize raw carrier values into a target carrier for order tracking.",
      synonyms: [
        "carrier mappings",
        "carrier normalization",
        "map carriers",
        "target carrier",
      ],
    }),
  }),
  ...orderTrackingSettingsGroup("order_tracking_settings.weekly_email", {
    "orderTracking/weeklyAnalyticsEmail/enabled": setting({
      label: "Weekly email update",
      description:
        "Send a weekly recap email of order-to-delivery, fulfillment, and transit times plus flagged problem orders.",
      synonyms: [
        "weekly email",
        "weekly recap email",
        "analytics email",
        "weekly update",
        "send me a weekly recap of delivery performance",
        "weekly recap of delivery performance",
        "weekly delivery performance recap",
        "delivery performance email",
      ],
    }),
    "orderTracking/weeklyAnalyticsEmail/recipientEmails": setting({
      label: "Weekly email recipients",
      description:
        "Email addresses that receive the weekly order tracking recap email.",
      synonyms: [
        "weekly email recipients",
        "recipient emails",
        "weekly recap recipients",
      ],
    }),
  }),
};

type OrderTrackingSettingsFlatTeamSettingKey =
  keyof typeof OrderTrackingSettingsRegistry;
// eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
const OrderTrackingSettingsFlatTeamSettingKeys = Object.keys(
  OrderTrackingSettingsRegistry,
) as OrderTrackingSettingsFlatTeamSettingKey[];

export const OrderTrackingSettingsFlatTeamSettingCatalogDefinitions =
  OrderTrackingSettingsFlatTeamSettingKeys.map((key) => {
    const definition = OrderTrackingSettingsRegistry[key];
    return {
      id: key,
      groupId: definition.group,
      metadata: createSettingMetadataFromGroupDefaults({
        setting: definition,
        group: OrderTrackingSettingsFlatTeamSettingGroupById[definition.group],
      }),
      valueSource: { adapter: "teamSettings", key },
    };
  }) satisfies readonly SettingDefinition<
    OrderTrackingSettingsFlatTeamSettingKey,
    OrderTrackingSettingsFlatTeamSettingGroupId,
    TeamSettingsSettingValueSource<OrderTrackingSettingsFlatTeamSettingKey>
  >[];

export const OrderTrackingSettingsFlatTeamSettingMetadataByKey =
  // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
  Object.fromEntries(
    OrderTrackingSettingsFlatTeamSettingCatalogDefinitions.map((definition) => [
      definition.id,
      definition.metadata,
    ]),
  ) as Record<OrderTrackingSettingsFlatTeamSettingKey, SettingMetadata>;
